/**
 * useAgent - A high-level React hook for interacting with A2A agents
 *
 * This hook provides a simple, user-friendly API for common agent interactions
 * without requiring knowledge of A2A protocol internals. It builds on top of
 * useA2AAgent and adds:
 * - Conversation management with friendly names
 * - Simple send/receive messaging
 * - JSON response parsing with generics
 * - Streaming with callbacks
 * - Optional message history tracking
 * - Persistence via localforage
 * - Retry policies
 *
 * @see useA2AAgent for the lower-level A2A protocol wrapper
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import { useState, useCallback, useRef, useEffect } from 'react';
import { roleToJSON, taskStateToJSON } from '@a2a-js/sdk';
import { useA2AAgent, buildMessageParams, extractMessageText, isMessage, isTask, isStatusUpdateEvent, isTaskTerminal, getTaskStatus, TERMINAL_TASK_STATES, } from './useA2AAgent';
/**
 * Default retry policy
 */
export const DEFAULT_RETRY_POLICY = {
    maxRetries: 3,
    initialDelayMs: 1000,
    backoffMultiplier: 2,
    maxDelayMs: 10000,
    isRetryable: (error) => {
        var _a;
        // Don't retry user-initiated cancellations
        if (error.name === 'AbortError')
            return false;
        // Network errors from the Fetch API surface as TypeError
        if (error.name === 'TypeError')
            return true;
        // Check for structured HTTP status property (A2A SDK, Axios, etc.)
        const errorRecord = error;
        const statusCode = (_a = errorRecord.status) !== null && _a !== void 0 ? _a : errorRecord.statusCode;
        if (typeof statusCode === 'number') {
            return statusCode >= 500 && statusCode < 600;
        }
        // Fallback: match common transient-error patterns in the message
        const message = error.message.toLowerCase();
        return (message.includes('network') ||
            message.includes('fetch') ||
            message.includes('timeout') ||
            message.includes('etimedout') ||
            message.includes('econnreset') ||
            /\b5\d{2}\b/.test(message));
    },
};
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Sanitize a URL to use as a storage key prefix
 */
function sanitizeForStorageKey(url) {
    return url
        .replace(/^https?:\/\//, '')
        .replace(/[^a-zA-Z0-9]/g, '_')
        .substring(0, 50);
}
/**
 * Sleep for a given duration
 */
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Execute a function with retry logic
 */
function withRetry(fn, policy, signal) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        if (!policy) {
            return fn();
        }
        let lastError;
        let delay = policy.initialDelayMs;
        for (let attempt = 0; attempt <= policy.maxRetries; attempt++) {
            try {
                if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                    throw new Error('Request aborted');
                }
                return yield fn();
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                // Don't retry if we've exhausted attempts or error is not retryable
                const isLastAttempt = attempt === policy.maxRetries;
                const isRetryable = (_b = (_a = policy.isRetryable) === null || _a === void 0 ? void 0 : _a.call(policy, lastError)) !== null && _b !== void 0 ? _b : true;
                if (isLastAttempt || !isRetryable || (signal === null || signal === void 0 ? void 0 : signal.aborted)) {
                    throw lastError;
                }
                // Wait before retrying
                yield sleep(Math.min(delay, policy.maxDelayMs));
                delay *= policy.backoffMultiplier;
            }
        }
        throw lastError;
    });
}
/**
 * Convert a stored conversation back to a Conversation object
 */
function deserializeConversation(stored) {
    var _a;
    return Object.assign(Object.assign({}, stored), { createdAt: new Date(stored.createdAt), updatedAt: new Date(stored.updatedAt), history: (_a = stored.history) === null || _a === void 0 ? void 0 : _a.map((msg) => (Object.assign(Object.assign({}, msg), { timestamp: new Date(msg.timestamp) }))) });
}
/**
 * Convert a Conversation to a storable format
 */
function serializeConversation(conv) {
    var _a;
    return Object.assign(Object.assign({}, conv), { createdAt: conv.createdAt.toISOString(), updatedAt: conv.updatedAt.toISOString(), history: (_a = conv.history) === null || _a === void 0 ? void 0 : _a.map((msg) => (Object.assign(Object.assign({}, msg), { timestamp: msg.timestamp.toISOString() }))) });
}
/**
 * Try to parse text as JSON
 */
function tryParseJson(text) {
    try {
        // Try to find JSON in the response (may be wrapped in markdown code blocks)
        let jsonStr = text;
        // Check for markdown code blocks
        const jsonBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (jsonBlockMatch) {
            jsonStr = jsonBlockMatch[1].trim();
        }
        return JSON.parse(jsonStr);
    }
    catch (_a) {
        return null;
    }
}
// ============================================================================
// Hook Implementation
// ============================================================================
/**
 * A high-level React hook for interacting with A2A agents.
 *
 * Provides a simple API for common operations without requiring
 * knowledge of A2A protocol internals.
 *
 * @example
 * ```tsx
 * // Basic usage
 * const agent = useAgent({ agentUrl: 'https://my-agent.example.com' });
 *
 * const response = await agent.send("Hello!");
 * console.log(response.text);
 * ```
 *
 * @example
 * ```tsx
 * // With JSON parsing
 * interface WeatherData { temperature: number; conditions: string; }
 *
 * const weather = await agent.sendJson<WeatherData>("Get weather as JSON");
 * console.log(weather.temperature);
 * ```
 *
 * @example
 * ```tsx
 * // With persistence
 * import localforage from 'localforage';
 *
 * const agent = useAgent({
 *   agentUrl: 'https://my-agent.example.com',
 *   storage: localforage,
 *   trackHistory: true,
 * });
 * ```
 */
export function useAgent(options) {
    const { agentUrl, factoryConfig, defaultConversation = 'default', storage, storageKeyPrefix = sanitizeForStorageKey(agentUrl), retryPolicy = DEFAULT_RETRY_POLICY, trackHistory = false, } = options;
    // Get the underlying A2A agent
    const a2aAgent = useA2AAgent({ agentUrl, factoryConfig });
    // State
    const [conversations, setConversations] = useState(new Map());
    const [activeConversation, setActiveConversationState] = useState(defaultConversation);
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(!!storage);
    // Refs for stable callbacks
    const conversationsRef = useRef(conversations);
    conversationsRef.current = conversations;
    // Storage key helpers
    const getStorageKey = useCallback((key) => `${storageKeyPrefix}:${key}`, [storageKeyPrefix]);
    // ============================================================================
    // Persistence
    // ============================================================================
    /**
     * Save conversations to storage
     */
    const saveToStorage = useCallback((convs) => __awaiter(this, void 0, void 0, function* () {
        if (!storage)
            return;
        try {
            const serialized = {};
            convs.forEach((conv, name) => {
                serialized[name] = serializeConversation(conv);
            });
            yield storage.setItem(getStorageKey('conversations'), serialized);
        }
        catch (error) {
            console.error('Failed to save conversations to storage:', error);
        }
    }), [storage, getStorageKey]);
    /**
     * Load conversations from storage
     */
    const loadFromStorage = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!storage) {
            setIsLoading(false);
            return;
        }
        try {
            const stored = yield storage.getItem(getStorageKey('conversations'));
            if (stored) {
                const loaded = new Map();
                Object.entries(stored).forEach(([name, conv]) => {
                    loaded.set(name, deserializeConversation(conv));
                });
                setConversations(loaded);
            }
        }
        catch (error) {
            console.error('Failed to load conversations from storage:', error);
        }
        finally {
            setIsLoading(false);
        }
    }), [storage, getStorageKey]);
    // Load from storage on mount
    useEffect(() => {
        loadFromStorage();
    }, [loadFromStorage]);
    // Check if agent is ready
    useEffect(() => {
        const checkReady = () => __awaiter(this, void 0, void 0, function* () {
            try {
                yield a2aAgent.getClient();
                setIsReady(true);
            }
            catch (_a) {
                // Will be ready once first request is made
            }
        });
        checkReady();
    }, [a2aAgent]);
    // ============================================================================
    // Conversation Management
    // ============================================================================
    /**
     * Update conversations state and persist
     */
    const updateConversations = useCallback((updater) => {
        setConversations((prev) => {
            const next = updater(new Map(prev));
            saveToStorage(next);
            return next;
        });
    }, [saveToStorage]);
    /**
     * Start a new conversation
     */
    const startConversation = useCallback((name) => {
        const existing = conversationsRef.current.get(name);
        if (existing) {
            return existing;
        }
        // Note: contextId and taskId are initially null and will be set by the server
        // on the first message response
        const conversation = {
            name,
            contextId: null,
            taskId: null,
            createdAt: new Date(),
            updatedAt: new Date(),
            messageCount: 0,
            history: trackHistory ? [] : undefined,
        };
        updateConversations((prev) => {
            const next = new Map(prev);
            next.set(name, conversation);
            return next;
        });
        return conversation;
    }, [trackHistory, updateConversations]);
    /**
     * Get a conversation by name
     */
    const getConversation = useCallback((name) => {
        return conversationsRef.current.get(name);
    }, []);
    /**
     * List all conversations
     */
    const listConversations = useCallback(() => {
        return Array.from(conversationsRef.current.values());
    }, []);
    /**
     * End a conversation
     */
    const endConversation = useCallback((name) => {
        updateConversations((prev) => {
            const next = new Map(prev);
            next.delete(name);
            return next;
        });
        // Switch to default if ending active conversation
        if (activeConversation === name) {
            setActiveConversationState(defaultConversation);
        }
    }, [activeConversation, defaultConversation, updateConversations]);
    /**
     * Clear all conversations
     */
    const clearAllConversations = useCallback(() => {
        updateConversations(() => new Map());
        setActiveConversationState(defaultConversation);
    }, [defaultConversation, updateConversations]);
    /**
     * Set active conversation (creates if doesn't exist)
     */
    const setActiveConversation = useCallback((name) => {
        if (!conversationsRef.current.has(name)) {
            startConversation(name);
        }
        setActiveConversationState(name);
    }, [startConversation]);
    /**
     * Get or create a conversation for sending
     */
    const getOrCreateConversation = useCallback((name) => {
        const existing = conversationsRef.current.get(name);
        if (existing) {
            return existing;
        }
        return startConversation(name);
    }, [startConversation]);
    /**
     * Update a conversation after a message
     */
    const updateConversationAfterMessage = useCallback((name, updates, userMessage, agentMessage, agentData) => {
        updateConversations((prev) => {
            const next = new Map(prev);
            const conv = next.get(name);
            if (!conv)
                return prev;
            const updatedConv = Object.assign(Object.assign(Object.assign({}, conv), updates), { updatedAt: new Date(), messageCount: conv.messageCount + 1 });
            // Add to history if tracking (immutable — create new array, never push)
            if (trackHistory && updatedConv.history) {
                const additions = [];
                if (userMessage) {
                    additions.push({
                        id: crypto.randomUUID(),
                        role: 'user',
                        text: userMessage,
                        timestamp: new Date(),
                    });
                }
                if (agentMessage) {
                    additions.push({
                        id: crypto.randomUUID(),
                        role: 'agent',
                        text: agentMessage,
                        timestamp: new Date(),
                        data: agentData,
                    });
                }
                if (additions.length > 0) {
                    updatedConv.history = [...updatedConv.history, ...additions];
                }
            }
            next.set(name, updatedConv);
            return next;
        });
    }, [trackHistory, updateConversations]);
    // ============================================================================
    // Messaging
    // ============================================================================
    /**
     * Send a message and get a response
     */
    const send = useCallback((message, options) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const conversationName = (_a = options === null || options === void 0 ? void 0 : options.conversation) !== null && _a !== void 0 ? _a : activeConversation;
        const conversation = getOrCreateConversation(conversationName);
        const effectiveRetryPolicy = (options === null || options === void 0 ? void 0 : options.retryPolicy) !== undefined
            ? options.retryPolicy
            : retryPolicy;
        const response = yield withRetry(() => __awaiter(this, void 0, void 0, function* () {
            const params = buildMessageParams(message, {
                contextId: conversation.contextId,
                taskId: conversation.taskId,
            });
            return a2aAgent.sendMessage(params, { signal: options === null || options === void 0 ? void 0 : options.signal });
        }), effectiveRetryPolicy, options === null || options === void 0 ? void 0 : options.signal);
        // Extract response data
        let text = '';
        let status;
        let isFinal = true;
        let newTaskId = conversation.taskId;
        let newContextId = conversation.contextId;
        if (isMessage(response)) {
            text = extractMessageText(response);
            // Use server-provided contextId, falling back to existing if not provided.
            // A2A v1.0 materialises absent string fields as '' (proto3 defaults), so
            // `||` is required here - `??` would overwrite a good id with ''.
            newContextId = response.contextId || conversation.contextId;
            newTaskId = response.taskId || conversation.taskId;
        }
        else if (isTask(response)) {
            // Get text from the last agent message in history
            const agentMessages = (_c = (_b = response.history) === null || _b === void 0 ? void 0 : _b.filter((m) => roleToJSON(m.role) === 'ROLE_AGENT')) !== null && _c !== void 0 ? _c : [];
            const lastAgentMessage = agentMessages[agentMessages.length - 1];
            if (lastAgentMessage) {
                text = extractMessageText(lastAgentMessage);
            }
            status = getTaskStatus(response);
            isFinal = isTaskTerminal(response);
            newTaskId = response.id || conversation.taskId;
            newContextId = response.contextId || conversation.contextId;
        }
        // Update conversation state
        updateConversationAfterMessage(conversationName, { taskId: newTaskId, contextId: newContextId }, message, text);
        return {
            text,
            conversation: conversationName,
            isFinal,
            status,
            raw: response,
        };
    }), [
        activeConversation,
        getOrCreateConversation,
        retryPolicy,
        a2aAgent,
        updateConversationAfterMessage,
    ]);
    /**
     * Send a message and parse response as JSON
     */
    const sendJson = useCallback((message, options) => __awaiter(this, void 0, void 0, function* () {
        const response = yield send(message, options);
        const parsed = tryParseJson(response.text);
        if (parsed === null) {
            throw new Error(`Failed to parse agent response as JSON. Response: ${response.text.substring(0, 100)}...`);
        }
        // Validate if validator provided
        if ((options === null || options === void 0 ? void 0 : options.validate) && !options.validate(parsed)) {
            throw new Error(`Agent response JSON failed validation. Response: ${response.text.substring(0, 100)}...`);
        }
        return parsed;
    }), [send]);
    /**
     * Send a message with streaming
     */
    const sendStream = useCallback((message, callbacks, options) => __awaiter(this, void 0, void 0, function* () {
        var _d, _e, _f, _g;
        const conversationName = (_d = options === null || options === void 0 ? void 0 : options.conversation) !== null && _d !== void 0 ? _d : activeConversation;
        const conversation = getOrCreateConversation(conversationName);
        const effectiveRetryPolicy = (options === null || options === void 0 ? void 0 : options.retryPolicy) !== undefined
            ? options.retryPolicy
            : retryPolicy;
        let accumulatedText = '';
        let lastStatus;
        let isFinal = false;
        let rawResponse;
        let newTaskId = conversation.taskId;
        let newContextId = conversation.contextId;
        try {
            yield withRetry(() => __awaiter(this, void 0, void 0, function* () {
                var e_1, _h;
                var _j, _k, _l, _m, _o, _p, _q, _r, _s;
                // Reset accumulators at the start of each attempt so partial data
                // from a failed stream does not bleed into the retry.
                accumulatedText = '';
                lastStatus = undefined;
                isFinal = false;
                rawResponse = undefined;
                const params = buildMessageParams(message, {
                    contextId: conversation.contextId,
                    taskId: conversation.taskId,
                });
                const stream = a2aAgent.sendMessageStream(params, {
                    signal: options === null || options === void 0 ? void 0 : options.signal,
                });
                try {
                    for (var stream_1 = __asyncValues(stream), stream_1_1; stream_1_1 = yield stream_1.next(), !stream_1_1.done;) {
                        const event = stream_1_1.value;
                        if (isMessage(event)) {
                            const text = extractMessageText(event);
                            accumulatedText = text;
                            (_j = callbacks.onText) === null || _j === void 0 ? void 0 : _j.call(callbacks, text, accumulatedText);
                            rawResponse = event;
                            // v1.0 proto3 defaults absent strings to '' - `||` not `??`.
                            newContextId = event.contextId || conversation.contextId;
                            newTaskId = event.taskId || conversation.taskId;
                        }
                        else if (isTask(event)) {
                            const agentMessages = (_l = (_k = event.history) === null || _k === void 0 ? void 0 : _k.filter((m) => roleToJSON(m.role) === 'ROLE_AGENT')) !== null && _l !== void 0 ? _l : [];
                            const lastAgentMessage = agentMessages[agentMessages.length - 1];
                            if (lastAgentMessage) {
                                const text = extractMessageText(lastAgentMessage);
                                if (text !== accumulatedText) {
                                    accumulatedText = text;
                                    (_m = callbacks.onText) === null || _m === void 0 ? void 0 : _m.call(callbacks, text, accumulatedText);
                                }
                            }
                            lastStatus = getTaskStatus(event);
                            isFinal = isTaskTerminal(event);
                            (_o = callbacks.onStatus) === null || _o === void 0 ? void 0 : _o.call(callbacks, lastStatus, isFinal);
                            rawResponse = event;
                            newTaskId = event.id || conversation.taskId;
                            newContextId = event.contextId || conversation.contextId;
                        }
                        else if (isStatusUpdateEvent(event)) {
                            const state = (_p = event.status) === null || _p === void 0 ? void 0 : _p.state;
                            lastStatus = state === undefined ? 'unknown' : taskStateToJSON(state);
                            // A2A v1.0 removed TaskStatusUpdateEvent.final; derive it from terminal states.
                            isFinal = TERMINAL_TASK_STATES.includes(lastStatus);
                            (_q = callbacks.onStatus) === null || _q === void 0 ? void 0 : _q.call(callbacks, lastStatus, isFinal);
                            // Extract text from status message if present
                            const statusMessage = (_r = event.status) === null || _r === void 0 ? void 0 : _r.message;
                            if (statusMessage) {
                                const text = extractMessageText(statusMessage);
                                if (text && text !== accumulatedText) {
                                    accumulatedText += text;
                                    (_s = callbacks.onText) === null || _s === void 0 ? void 0 : _s.call(callbacks, text, accumulatedText);
                                }
                            }
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (stream_1_1 && !stream_1_1.done && (_h = stream_1.return)) yield _h.call(stream_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            }), effectiveRetryPolicy, options === null || options === void 0 ? void 0 : options.signal);
        }
        catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            (_e = callbacks.onError) === null || _e === void 0 ? void 0 : _e.call(callbacks, err);
            throw err;
        }
        if (!rawResponse) {
            const err = new Error("Streaming completed without receiving a Message or Task event.");
            (_f = callbacks.onError) === null || _f === void 0 ? void 0 : _f.call(callbacks, err);
            throw err;
        }
        // Update conversation state
        updateConversationAfterMessage(conversationName, { taskId: newTaskId, contextId: newContextId }, message, accumulatedText);
        const response = {
            text: accumulatedText,
            conversation: conversationName,
            isFinal,
            status: lastStatus,
            raw: rawResponse,
        };
        (_g = callbacks.onComplete) === null || _g === void 0 ? void 0 : _g.call(callbacks, response);
        return response;
    }), [
        activeConversation,
        getOrCreateConversation,
        retryPolicy,
        a2aAgent,
        updateConversationAfterMessage,
    ]);
    // ============================================================================
    // Agent Info
    // ============================================================================
    /**
     * Get agent information
     */
    const getAgentInfo = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _t, _u, _v, _w;
        const card = yield a2aAgent.getAgentCard();
        return {
            name: card.name,
            description: card.description,
            version: card.version,
            capabilities: {
                streaming: (_u = (_t = card.capabilities) === null || _t === void 0 ? void 0 : _t.streaming) !== null && _u !== void 0 ? _u : false,
                pushNotifications: (_w = (_v = card.capabilities) === null || _v === void 0 ? void 0 : _v.pushNotifications) !== null && _w !== void 0 ? _w : false,
            },
        };
    }), [a2aAgent]);
    // ============================================================================
    // History
    // ============================================================================
    /**
     * Get message history for a conversation
     */
    const getHistory = useCallback((conversationName) => {
        var _a;
        const name = conversationName !== null && conversationName !== void 0 ? conversationName : activeConversation;
        const conversation = conversationsRef.current.get(name);
        return (_a = conversation === null || conversation === void 0 ? void 0 : conversation.history) !== null && _a !== void 0 ? _a : [];
    }, [activeConversation]);
    // ============================================================================
    // Return
    // ============================================================================
    return {
        // Conversation Management
        startConversation,
        getConversation,
        listConversations,
        endConversation,
        clearAllConversations,
        // Messaging
        send,
        sendJson,
        sendStream,
        // Agent Info
        getAgentInfo,
        // Status
        isReady,
        isLoading,
        activeConversation,
        setActiveConversation,
        getHistory,
    };
}
export default useAgent;
